import { useId, type ReactNode } from 'react';

import FormField from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FormSpacing } from '@/theme/size-tokens';

export type FormSelectProps = {
  /** HTML name attribute used when submitting forms */
  name?: string;
  /** Current selected value (controlled) */
  value?: string;
  /** Called with the new value when selection changes */
  onValueChange?: (value: string) => void;
  /** Legacy onChange handler — extracts value from event for backward compat */
  onChange?: (e: { target: { value: string } }) => void;
  /** Disables select interaction */
  disabled?: boolean;
  /** HTML id attribute */
  id?: string;
  /** Marks select as required */
  required?: boolean;
  /** SelectItem children */
  children?: ReactNode;
  /** Label text displayed above the select */
  label?: string;
  /** Helper text displayed below select */
  helperText?: string;
  /** Spacing between label, select, and helper text */
  spacing?: FormSpacing;
  /** Error message to display */
  error?: string;
  /** Placeholder text for the select trigger */
  selectLabel?: string;
  /** Content rendered at the bottom of the dropdown, outside the scrollable viewport */
  footer?: ReactNode;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
};

function FormSelect({
  name,
  label,
  helperText,
  spacing,
  error,
  required,
  disabled,
  id,
  selectLabel,
  value,
  onValueChange,
  onChange,
  children,
  footer,
  labelSuffix,
}: Readonly<FormSelectProps>) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const handleValueChange = (v: string) => {
    onValueChange?.(v);
    onChange?.({ target: { value: v } });
  };

  return (
    <FormField
      label={label}
      helperText={helperText}
      spacing={spacing}
      error={error}
      required={required}
      disabled={disabled}
      id={selectId}
      labelSuffix={labelSuffix}
    >
      <Select
        name={name}
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            helperText || error ? `${selectId}-message` : undefined
          }
        >
          <SelectValue placeholder={selectLabel ?? 'Select...'} />
        </SelectTrigger>
        <SelectContent footer={footer}>{children}</SelectContent>
      </Select>
    </FormField>
  );
}

FormSelect.displayName = 'FormSelect';

export default FormSelect;
